/**
 * UpdateAvailableToast — upozorní uživatele, že je nasazená nová verze a stránku
 * stačí reloadnout.
 *
 * Trigger vs. gate (anti-falešná-aktualizace):
 *   - **Trigger** = SW `controllerchange` (nový service worker převzal kontrolu).
 *     Sám o sobě je odpojený od čísla verze → naskočil i při deployi bez bumpu.
 *   - **Gate** = porovnání **build-id**: `RUNNING_BUILD_ID` (zapečené v běžícím
 *     bundle) vs. `/version.json` (právě nasazený build). Toast se ukáže JEN když
 *     se build-id liší = reálně nová verze. Tím odpadá matoucí „0.5.6 → 0.5.6"
 *     i first-install falešný controllerchange (tam je running === deployed).
 *   - **Popisek** WEB jde z pravdivého zdroje: `RUNNING_WEB` (běžící) → deployed
 *     web verze. Když jsou shodné (deploy bez bumpu), ukáže „nový build · čas".
 *     API řádek zůstává informativně z `/healthz`.
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";
import {
  fetchServerVersions,
  getBaselineVersions,
  fetchDeployedBuild,
  fetchChangelog,
  newerEntries,
  RUNNING_BUILD_ID,
  RUNNING_WEB,
  type VersionPair,
  type DeployedBuild,
  type ChangelogEntry,
} from "../lib/version-tracking.js";

export function UpdateAvailableToast() {
  const [visible, setVisible] = useState(false);
  const [deployed, setDeployed] = useState<DeployedBuild | null>(null);
  // API verze (informativně) z /healthz — web řešíme přes build-id (pravdivý zdroj).
  const [apiBaseline, setApiBaseline] = useState<VersionPair | null>(null);
  const [apiNext, setApiNext] = useState<VersionPair | null>(null);
  // „Co je nového" — záznamy changelogu novější než běžící verze.
  const [news, setNews] = useState<ChangelogEntry[]>([]);
  // Zabraň souběžnému zpracování více controllerchange.
  const handlingRef = useRef(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    const handler = () => {
      if (handlingRef.current) return;
      handlingRef.current = true;
      void (async () => {
        const dep = await fetchDeployedBuild();
        handlingRef.current = false;
        // GATE: žádná reálná změna bundle (nebo version.json nedostupný) → nic.
        // Pokrývá i first-install (running === deployed) → žádný falešný toast.
        if (!dep || dep.buildId === RUNNING_BUILD_ID) return;
        const baseline = getBaselineVersions();
        setDeployed(dep);
        setApiBaseline(baseline);
        void fetchServerVersions().then((v) => setApiNext(v));
        void fetchChangelog().then((all) =>
          setNews(newerEntries(all, RUNNING_WEB, baseline?.api ?? null)),
        );
        setVisible(true);
      })();
    };

    navigator.serviceWorker.addEventListener("controllerchange", handler);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", handler);
    };
  }, []);

  if (!visible || !deployed) return null;

  // Web: shodná verze ale jiný build → „nový build · čas" místo matoucího „X→X".
  const webChanged = deployed.web !== RUNNING_WEB;
  const builtAtLabel = deployed.builtAt
    ? new Date(deployed.builtAt).toLocaleString("cs-CZ", {
        day: "numeric",
        month: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto"
    >
      <div className="bg-card border border-primary/40 rounded-xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <Sparkles size={20} className="text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">Nová verze Beer Wars</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Aktualizace je připravena. Klikni pro načtení.
            </p>
            <div className="mt-2 text-[11px] font-mono grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
              <span className="text-muted-foreground">WEB</span>
              <span>
                {webChanged ? (
                  <>
                    <span className="text-muted-foreground/70">{RUNNING_WEB}</span>
                    <span className="text-primary mx-1">→</span>
                    <span className="text-foreground font-semibold">{deployed.web}</span>
                  </>
                ) : (
                  <span className="text-foreground">
                    {RUNNING_WEB} · nový build{builtAtLabel ? ` · ${builtAtLabel}` : ""}
                  </span>
                )}
              </span>
              {apiNext && apiBaseline && apiNext.api !== apiBaseline.api && (
                <>
                  <span className="text-muted-foreground">API</span>
                  <span>
                    <span className="text-muted-foreground/70">{apiBaseline.api}</span>
                    <span className="text-primary mx-1">→</span>
                    <span className="text-foreground font-semibold">{apiNext.api}</span>
                  </span>
                </>
              )}
            </div>
            {news.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto rounded-lg bg-muted/50 p-2">
                <p className="text-[11px] font-semibold text-muted-foreground mb-1">Co je nového</p>
                <ul className="space-y-1">
                  {news.slice(0, 6).map((e, i) => (
                    <li
                      key={`${e.date}-${i}`}
                      className="flex gap-1.5 text-[11px] text-foreground/90"
                    >
                      <span className="text-primary">•</span>
                      <span>{e.summary}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn btn-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                Načíst novou verzi
              </button>
              <button
                type="button"
                onClick={() => setVisible(false)}
                className="btn btn-sm px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
                aria-label="Zavřít upozornění"
              >
                Později
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setVisible(false)}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Zavřít"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
